import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, Typography } from '@material-ui/core';
import InsertPhotoIcon from '@material-ui/icons/InsertPhoto';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import ImageIcon from '@material-ui/icons/Image';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { useApolloClient } from '@apollo/client';
import ModalUtils from 'utils/ModalUtils';
import { getMMSFileType, openMessageAttachment } from 'utils/MMSUtils';

const useStyles = makeStyles((theme) => ({
    card: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 1.5),
        marginTop: theme.spacing(1),
        borderRadius: theme.spacing(0.75),
        backgroundColor: theme.palette.background.white,
        border: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        color: 'inherit',
        transition: 'background-color 0.2s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    disabled: {
        cursor: 'wait',
        opacity: 0.7,
    },
    icon: {
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    fileName: {
        flex: 1,
        fontSize: theme.spacing(1.75),
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
    },
}));

const getFileIcon = (fileType) => {
    switch (fileType) {
    case 'pdf':
        return PictureAsPdfIcon;
    case 'gif':
        return ImageIcon;
    case 'image':
        return InsertPhotoIcon;
    default:
        return AttachFileIcon;
    }
};

const MMSMediaCard = ({ attachment, crmId, className }) => {
    const classes = useStyles();
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);
    const fileName = attachment?.fileName || 'attachment';
    const fileType = getMMSFileType(fileName, attachment?.mimeType);
    const FileIcon = getFileIcon(fileType);

    const handleOpen = async () => {
        if (loading) return;
        if (!crmId) {
            ModalUtils.errorMessage(null, 'Unable to open attachment: missing CRM id.');
            return;
        }

        try {
            setLoading(true);
            await openMessageAttachment(client, crmId, fileName);
        } catch (e) {
            ModalUtils.errorMessage(null, e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={handleOpen}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpen();
                }
            }}
            className={clsx(classes.card, className, { [classes.disabled]: loading })}
        >
            <FileIcon className={classes.icon} fontSize="small" />
            <Typography className={classes.fileName} variant="body2">
                {fileName}
            </Typography>
        </div>
    );
};

MMSMediaCard.propTypes = {
    attachment: PropTypes.shape({
        messageAttachmentId: PropTypes.string,
        messageId: PropTypes.string,
        fileName: PropTypes.string,
        mimeType: PropTypes.string,
        size: PropTypes.number,
        createdOn: PropTypes.string,
    }).isRequired,
    crmId: PropTypes.string,
    className: PropTypes.string,
};

MMSMediaCard.defaultProps = {
    crmId: null,
    className: null,
};

export default MMSMediaCard;
